// ScatterChart.component.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  TimeScale,
  type ChartOptions,
  type TooltipItem,
  type Plugin,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import { format } from "date-fns";
import { es, enUS, pt, fr, de, it, ca } from "date-fns/locale";
import './ScatterChart.scss';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend, TimeScale);

const LOCALES = { es, en: enUS, pt, fr, de, it, ca } as const;

export type DailyPoint = {
  day: string; // YYYY-MM-DD
  y: number;
  meta?: Record<string, any>;
};

export type DailySeries = {
  id: string;
  label: string;
  color: string;
  points: DailyPoint[];
  pointRadius?: number;
};

type Props = {
  from: string;
  to: string;
  series: DailySeries[];

  locale?: keyof typeof LOCALES;
  yLabel?: string;
  yMin?: number;
  yMax?: number;
  height?: number | string;

  fillMissingDaysWithZero?: boolean;

  // threshold
  thresholdEnabled?: boolean;
  thresholdLabel?: string;
  thresholdValue?: number;
  thresholdStep?: number;
  onThresholdChange?: (v: number) => void;

  onPointClick?: (payload: { seriesId: string; point: DailyPoint }) => void;
};

function dayToMs(day: string): number {
  const [yy, mm, dd] = day.split("-").map(Number);
  return new Date(yy, (mm ?? 1) - 1, dd ?? 1, 0, 0, 0, 0).getTime();
}

function daysBetween(from: string, to: string): string[] {
  const res: string[] = [];
  const start = new Date(dayToMs(from));
  const end = new Date(dayToMs(to));
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start > end) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    res.push(`${yy}-${mm}-${dd}`);
  }
  return res;
}

// Plugin: línia horitzontal al threshold
const thresholdLinePlugin: Plugin<"scatter"> = {
  id: "thresholdLine",
  afterDatasetsDraw(chart, _args, pluginOptions: any) {
    const yScale = chart.scales.y;
    if (!yScale) return;

    const threshold = pluginOptions?.threshold;
    const enabled = pluginOptions?.enabled !== false;

    if (!enabled || typeof threshold !== "number") return;
    if (threshold < yScale.min || threshold > yScale.max) return;

    const y = yScale.getPixelForValue(threshold);
    const { ctx, chartArea } = chart;

    const accentColor = getComputedStyle(document.documentElement)
      .getPropertyValue("--accent-color")
      .trim() || "#00c389";

    ctx.save();
    ctx.lineWidth = 1;
    // ctx.setLineDash([6, 6]);
    ctx.strokeStyle = accentColor;
    ctx.beginPath();
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.setLineDash([]);

    // label a la dreta (opcional)
    const label = pluginOptions?.label ?? "";
    if (label) {
      ctx.fillStyle = "var(--accent-color)";
      ctx.font = "700 12px sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.fillText(`${threshold}`, chartArea.right - 4, y + 18);
    }

    ctx.restore();
  },
};

export default function ScatterChart({
  from,
  to,
  series,
  locale = "es",
  yLabel,
  yMin = 0,
  yMax = 1,
  height = 420,
  fillMissingDaysWithZero = false,

  thresholdEnabled = true,
  thresholdLabel = "Threshold",
  thresholdValue = 0.25,
  thresholdStep = 0.01,
  onThresholdChange,

  onPointClick,
}: Props) {
  const dayKeys = useMemo(() => daysBetween(from, to), [from, to]);
  const daySet = useMemo(() => new Set(dayKeys), [dayKeys]);

  const [threshold, setThreshold] = useState<number>(thresholdValue);

  useEffect(() => {
    setThreshold(thresholdValue);
  }, [thresholdValue]);

  const chartData = useMemo(() => {
    const datasets = series.map((s) => {
      const map = new Map<string, DailyPoint>();
      s.points.forEach((p) => {
        if (daySet.has(p.day)) map.set(p.day, p);
      });

      const finalPoints: Array<{ x: number; y: number; meta?: any; day: string }> = [];

      if (fillMissingDaysWithZero) {
        dayKeys.forEach((day) => {
          const p = map.get(day);
          if (p) finalPoints.push({ x: dayToMs(day), y: p.y, meta: p.meta, day });
          else finalPoints.push({ x: dayToMs(day), y: 0, meta: { missing: true }, day });
        });
      } else {
        map.forEach((p, day) => finalPoints.push({ x: dayToMs(day), y: p.y, meta: p.meta, day }));
        finalPoints.sort((a, b) => a.x - b.x);
      }

      return {
        label: s.label,
        data: finalPoints,
        borderColor: s.color,
        backgroundColor: s.color,
        pointRadius: s.pointRadius ?? 3,
        pointHoverRadius: (s.pointRadius ?? 3) + 1,
      };
    });

    return { datasets };
  }, [series, dayKeys, daySet, fillMissingDaysWithZero]);

  const xMin = useMemo(() => dayToMs(from), [from]);
  const xMax = useMemo(() => dayToMs(to) + 24 * 60 * 60 * 1000 - 1, [to]);

  const options: ChartOptions<"scatter"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      parsing: { xAxisKey: "x", yAxisKey: "y" },
      scales: {
        x: {
          type: "time",
          min: xMin,
          max: xMax,
          time: { unit: "day", tooltipFormat: "dd/MM/yyyy", displayFormats: { day: "dd/MM" } },
          ticks: { autoSkip: true, maxTicksLimit: 8, maxRotation: 0 },
        },
        y: {
          min: yMin,
          max: yMax,
          title: yLabel ? { display: true, text: yLabel } : undefined,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          intersect: false,
          mode: "nearest",
          callbacks: {
            title: (items: TooltipItem<"scatter">[]) => {
              if (!items.length) return "";
              const loc = LOCALES[locale] ?? LOCALES.es;
              return format(new Date(items[0].parsed.x), "dd/MM/yyyy", { locale: loc });
            },
            label: (ctx) => {
              const y = ctx.parsed.y;
              const label = ctx.dataset.label ?? "";
              return label ? `${label}: ${y}` : `Y: ${y}`;
            },
            afterLabel: (ctx) => {
              const raw: any = ctx.raw;
              const meta = raw?.meta;
              if (!meta || meta.missing) return "";
              const parts: string[] = [];
              if (meta.runId) parts.push(`ID: ${meta.runId}`);
              if (meta.severity) parts.push(`Sev: ${meta.severity}`);
              return parts.join(" • ");
            },
          },
        },

        // 👇 plugin options
        thresholdLine: {
          enabled: thresholdEnabled,
          threshold,
          color: "var(--accent-color)",
          label: thresholdLabel,
        } as any,
      },
      onClick: (_evt, elements) => {
        if (!onPointClick || !elements?.length) return;

        const el = elements[0];
        const dsIndex = el.datasetIndex;
        const idx = el.index;

        const ds: any = (chartData as any).datasets[dsIndex];
        const rawPoint = ds?.data?.[idx];
        if (!rawPoint) return;

        const s = series[dsIndex];
        onPointClick({
          seriesId: s.id,
          point: { day: rawPoint.day, y: rawPoint.y, meta: rawPoint.meta },
        });
      },
    }),
    [
      chartData,
      locale,
      onPointClick,
      series,
      xMin,
      xMax,
      yLabel,
      yMin,
      yMax,
      thresholdEnabled,
      threshold,
      thresholdLabel,
    ]
  );

  const onChangeThreshold = (v: number) => {
    setThreshold(v);
    onThresholdChange?.(v);
  };

  return (
    <div style={{ height, display: "flex", gap: 12 }}>
      {/* Chart */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Scatter data={chartData} options={options} plugins={[thresholdLinePlugin]} />
      </div>

      {/* Slider vertical (scroll bar) */}
      {thresholdEnabled && (
        <div
          style={{
            width: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingRight: 4,
          }}
          title={`${threshold.toFixed(2)}`}
        >
          <input
            type="range"
            className="thresholdSlider"
            min={yMin}
            max={yMax}
            step={thresholdStep}
            value={threshold}
            onChange={(e) => onChangeThreshold(Number(e.target.value))}
            style={{
              writingMode: "vertical-rl",
              height: "100%",
              width: 18,
              transform: "rotate(180deg)",
            }}
          />
        </div>
      )}
    </div>
  );
}
